import React from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import depurators from '../assets/products/depurators.jpeg';
import fracSystems from '../assets/products/frac-accumulator-systems.jpeg';
import structuralWeldments from '../assets/products/structural-weldments-and-cages.jpg';
import cabinets from '../assets/products/cabinets-&-sheetmetal-work.jpg';
import spools from '../assets/products/spools-and-pressure-piping.jpeg';
import bopSystems from '../assets/products/bop-control-systems.jpeg';
import oilfieldSkids from '../assets/products/oilfield-skids-and-fabrication.jpeg';

const ProductDetail = () => {
  const { product } = useParams();

  const productData = {
    'bop-systems': {
      title: 'BOP Control Systems',
      description: 'Precision-engineered blowout preventer control systems designed for critical oilfield safety applications.',
      overview: 'Our BOP control systems are manufactured to the highest standards, ensuring reliable operation in the most demanding well control situations. Each system is thoroughly tested and certified to meet or exceed industry specifications.',
      features: [
        'High-pressure hydraulic control systems',
        'Redundant safety features',
        'Compliance with API specifications',
        'Custom configurations available',
        'Comprehensive testing and certification',
        'Field service and support',
      ],
      imagelink: bopSystems,
      applications: ['Onshore drilling operations', 'Offshore platforms', 'Well intervention', 'Workover operations'],
    },
    'frac-systems': {
      title: 'Frac Accumulator Systems',
      description: 'High-pressure hydraulic accumulator systems designed specifically for fracturing operations.',
      overview: 'Our frac accumulator systems provide reliable high-pressure hydraulic power for fracturing operations. Designed for durability and performance, these systems ensure consistent operation in demanding field conditions.',
      features: [
        'High-pressure accumulator banks',
        'Robust manifold systems',
        'Skid-mounted configurations',
        'Weather-resistant construction',
        'Easy maintenance access',
        'Custom pressure ratings',
      ],
      imagelink: fracSystems,
      applications: ['Hydraulic fracturing', 'Well stimulation', 'Pressure testing', 'Well control systems'],
    },
    'depurators': {
      title: 'Depurators',
      description: 'Custom-fabricated depuration systems for fluid separation and processing applications.',
      overview: 'Our depurators are engineered for efficient separation and processing of fluids in industrial applications. Custom-designed to meet specific operational requirements and flow rates.',
      features: [
        'Custom flow capacity design',
        'Corrosion-resistant materials',
        'Efficient separation technology',
        'Compact footprint options',
        'Easy maintenance design',
        'Multiple inlet/outlet configurations',
      ],
      imagelink: depurators,
      applications: ['Oil and gas processing', 'Water treatment', 'Industrial fluid processing', 'Wastewater management'],
    },
    'oilfield-skids': {
      title: 'Oilfield Skids and Fabrication',
      description: 'Complete turnkey skid packages and custom fabrication for oilfield operations.',
      overview: 'We design and fabricate complete skid-mounted systems for oilfield operations, providing turnkey solutions that arrive ready for installation and operation.',
      features: [
        'Turnkey skid packages',
        'Custom layout and design',
        'Integrated piping and controls',
        'Weather-resistant construction',
        'Lifting and transportation features',
        'Factory testing before shipment',
      ],
      imagelink: oilfieldSkids,
      applications: ['Pump skids', 'Manifold systems', 'Metering stations', 'Processing equipment'],
    },
    'structural-weldments': {
      title: 'Structural Weldments and Cages',
      description: 'Heavy-duty structural weldments, frames, and protective cages for industrial equipment.',
      overview: 'Our structural weldments and protective cages provide robust support and protection for industrial equipment. Engineered and certified to meet structural requirements.',
      features: [
        'Heavy structural steel fabrication',
        'Certified welding procedures',
        'Custom design engineering',
        'Protective coating options',
        'Compliance with structural codes',
        'Load testing available',
      ],
      imagelink: structuralWeldments,
      applications: ['Equipment supports', 'Protective cages', 'Structural frames', 'Platform assemblies'],
    },
    'cabinets': {
      title: 'Cabinets & Sheetmetal Work',
      description: 'Precision sheet metal fabrication including enclosures, cabinets, and custom housings.',
      overview: 'Our precision sheet metal fabrication services deliver custom enclosures, cabinets, and housings for electronics, controls, and industrial equipment.',
      features: [
        'Precision CNC punching and forming',
        'Custom powder coating finishes',
        'NEMA-rated enclosures',
        'Cable management features',
        'Hardware installation',
        'Rapid prototyping to production',
      ],
      imagelink: cabinets,
      applications: ['Control cabinets', 'Electronics enclosures', 'Equipment housings', 'Industrial panels'],
    },
    'spools': {
      title: 'Spools and Pressure Piping',
      description: 'Custom pipe spools and high-pressure piping systems for process applications.',
      overview: 'We fabricate custom pipe spools and pressure piping systems to precise specifications, with full traceability and testing documentation.',
      features: [
        'Custom pipe fabrication',
        'High-pressure ratings',
        'Material traceability',
        'Radiographic testing',
        'Hydrostatic testing',
        'Certified welding procedures',
      ],
      imagelink: spools,
      applications: ['Process piping', 'High-pressure systems', 'Offshore installations', 'Plant construction'],
    },
  };

  const currentProduct = productData[product] || productData['bop-systems'];

  return (
    <div className="min-h-screen pt-20" data-testid="product-detail-page">
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden h-[30rem]" data-testid="product-detail-hero">
        <div className="absolute inset-0">
          <img
            src={currentProduct.imagelink}
            alt={currentProduct.title}
            className="w-full h-full object-cover object-center"
          />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10 pt-24">
          <h1 className="text-4xl sm:text-5xl font-bold text-white heading-font mb-6" data-testid="product-detail-heading">
            {currentProduct.title}
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            {currentProduct.description}
          </p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="product-detail-content">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 heading-font mb-6">Overview</h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                {currentProduct.overview}
              </p>

              <h3 className="text-2xl font-bold text-gray-900 heading-font mb-4">Key Features</h3>
              <ul className="space-y-3 mb-8">
                {currentProduct.features.map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3" data-testid={`feature-${index}`}>
                    <CheckCircle2 className="w-6 h-6 text-blue-700 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 heading-font mb-4">Applications</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {currentProduct.applications.map((application, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4" data-testid={`application-${index}`}>
                    <span className="text-gray-700">{application}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-2xl p-8 sticky top-24">
                <h3 className="text-2xl font-bold text-gray-900 heading-font mb-6">Request Information</h3>
                <p className="text-gray-600 mb-6">
                  Interested in this product? Contact us for detailed specifications, pricing, and delivery information.
                </p>
                <Link
                  to="/contact"
                  className="block w-full text-center px-6 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 font-semibold transition-all shadow-md mb-4"
                  data-testid="product-detail-contact-button"
                >
                  Contact Us
                </Link>
                <Link
                  to="/products"
                  className="block w-full text-center px-6 py-3 bg-white text-blue-700 rounded-lg hover:bg-gray-100 font-semibold transition-all border-2 border-blue-700"
                  data-testid="product-detail-back-button"
                >
                  View All Products
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;